<template>
  <div class="container-fluid p-0">
    <div class="row g-0">
      <!-- Left side - Registration Form -->
      <div class="col-md-6 d-flex flex-column align-items-center justify-content-center bg-white px-4">
        <div class="card border-0 shadow-s w-100 mx-auto my-3 mx-3">
          <div class="card-body py-4">
            <!-- Logo -->
            <div class="mb-4">
              <div class="d-flex align-items-center">
               <img :src="brandLogo" :alt="brandName" class="mb-3" width="120"/>
              </div>
            </div>

            <!-- Form Heading -->
            <h2 class="fw-bold mb-4">Create an Account</h2>

            <!-- General Error Message -->
            <div v-if="generalError" class="alert alert-danger mb-3">
              {{ generalError }}
            </div>

            <!-- Registration Form -->
            <div class="mx-3">
              <!-- Full Name -->
              <div class="mb-3">
                <label for="fullname" class="form-label small fw-medium">Fullname*</label>
                <ArgonInput type="text" v-model="fullname" placeholder="Enter your fullname" required/>
                <div v-if="errors.fullname" class="text-danger small mt-1">
                  {{ errors.fullname[0] }}
                </div>
              </div>

              <!-- Email -->
              <div class="mb-3">
                <label for="email" class="form-label small fw-medium">Email Address*</label>
                <ArgonInput
                  type="email"
                  id="email"
                  v-model="email"
                  placeholder="Email Address"
                  required
                />
                <div v-if="errors.email" class="text-danger small">
                  {{ errors.email[0] }}
                </div>
              </div>

              <!-- Company Name -->
              <div class="mb-3">
                <label for="company" class="form-label small fw-medium">Company Name*</label>
                <ArgonInput
                    type="text"
                    v-model="company_name"
                    id="company"
                    placeholder="Company Name"
                    required
                  />
                <div v-if="errors.company_name" class="text-danger small">
                  {{ errors.company_name[0] }}
                </div>
              </div>

              <div class="mb-3">
                <label for="phone" class="form-label small fw-medium">Phone Number*</label>
                <ArgonInput
                    type="text"
                    v-model="phone"
                    @input="formatPhoneNumber"
                    id="phone"
                    placeholder="Phone Number"
                    required
                  />
                <div v-if="errors.phone" class="text-danger small">
                  {{ errors.phone[0] }}
                </div>
              </div>

              <!-- Create Password -->
              <div class="mb-3">
                <label for="password" class="form-label small fw-medium">Create Password*</label>
                <div class="input-group">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    id="password"
                    v-model="password"
                    placeholder="Create Strong Password"
                    required
                  >
                  <button
                    class="input-group-text bg-transparent border-start-0"
                    type="button"
                    @click="togglePasswordVisibility('password')"
                  >
                    <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye text-secondary" viewBox="0 0 16 16">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye-slash text-secondary" viewBox="0 0 16 16">
                      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                    </svg>
                  </button>
                </div>
                <div v-if="errors.password" class="text-danger small">
                  {{ errors.password[0] }}
                </div>
              </div>

              <!-- Confirm Password -->
              <div class="mb-4">
                <label for="confirm-password" class="form-label small fw-medium">Confirm Password*</label>
                <div class="input-group">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control"
                    id="confirm-password"
                    v-model="confirm_password"
                    placeholder="Confirm password"
                    required
                  >
                  <button
                    class="input-group-text bg-transparent border-start-0"
                    type="button"
                    @click="togglePasswordVisibility('confirmPassword')"
                  >
                    <svg v-if="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye text-secondary" viewBox="0 0 16 16">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye-slash text-secondary" viewBox="0 0 16 16">
                      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                    </svg>
                  </button>
                </div>
                <div v-if="errors.password_confirmation" class="text-danger small">
                  {{ errors.password_confirmation[0] }}
                </div>
              </div>

               <!-- Terms & Conditions Checkbox -->
              <div class="mb-3">
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="agreeTerms" 
                    v-model="agreedToTerms"
                  >
                  <label class="form-check-label small" for="agreeTerms">
                    I agree to the 
                    <a href="#" @click.prevent="openTermsModal" class="fw-bold text-decoration-none" style="color: #28a745 !important;">Terms & Conditions</a> 
                    and 
                    <a href="#" @click.prevent="openPrivacyModal" class="fw-bold text-decoration-none" style="color: #28a745 !important;">Privacy Policy</a>*
                  </label>
                </div>
                <div v-if="errors.agreedToTerms" class="text-danger small mt-1">
                  {{ errors.agreedToTerms[0] }}
                </div>
              </div>

              <!-- Cloudflare Turnstile Widget -->
              <div class="mb-3 d-flex justify-content-center">
                <div id="turnstile-container"></div>
              </div>
              <div v-if="errors.cf_turnstile_response" class="text-danger small mt-1 text-center mb-3">
                {{ errors.cf_turnstile_response[0] }}
              </div>

              <!-- Submit Button -->
              <div class="d-grid gap-2 mb-3">
                <argon-button @click="submitForm" class="btn btn-success btn-lg" :disabled="!agreedToTerms">Register</argon-button>
              </div>

              <!-- Login Link -->
              <div class="text-center mb-3">
                <small>Already have an account? <a href="/signin" class="text-success text-decoration-none fw-medium">Login Here</a></small>
              </div>
            </div>
          </div>
        </div>

        <!-- Left column footer - moved outside card but still within the left column, with gray background -->
        <!-- <div class="w-100 py-3 mt-auto">
          <div class="text-center">
            <small class="text-muted">
              © PT Anugrah Multi Remittance 2025 | <a href="#" class="text-muted text-decoration-none">Term & Conditions</a> | <a href="#" class="text-muted text-decoration-none">Privacy Policy</a>
            </small>
          </div>
        </div> -->
      </div>

      <!-- Right side - Modified to use direct IMG tag -->
      <div class="col-md-6 d-none d-md-block position-relative overflow-hidden" style="min-height: 100vh;">
        <!-- Using a direct img tag with overlay -->
        <div class="position-relative h-100">
          <img src="../../../public/register-image.png" alt="Registration Background"
               class="w-100 h-100 position-absolute" style="object-fit: cover;" />
        </div>
      </div>
    </div>
  </div>

  <TermsAndConditionsModal
    v-model="showTermsModal"
    show-agree
    @accept="acceptTerms"
  />

  <!-- Privacy Policy Modal -->
  <div v-if="showPrivacyModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);" @click.self="showPrivacyModal = false">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
                <div class="modal-header">
          <h5 class="modal-title" id="privacyModalLabel">Privacy Policy</h5>
          <button type="button" class="btn-close" @click="showPrivacyModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p class="fw-bold mb-3">Last Updated: July 21, 2025</p>
          
          <h6 class="fw-bold mt-4">1. Introduction</h6>
          <p>PT Anugrah Multi Remittance, hereinafter referred to as "{{ brandName }}", respects and is fully committed to protecting the privacy of our Users. This Privacy Policy explains how we collect, store, use, disclose, and protect personal data obtained through the use of {{ brandName }}' international remittance services.</p>
          <p>By using {{ brandName }}' services, the User is deemed to have read, understood, and agreed to the terms of this Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">2. Personal Data Collected</h6>
          <p>{{ brandName }} may collect the following types of information:</p>
          <p><strong>Identity Information:</strong> Full name, identification number (ID/Passport), date of birth, nationality.</p>
          <p><strong>Contact Information:</strong> Address, phone number, and email address.</p>
          <p><strong>Transaction Information:</strong> Amount and destination of the transfer, recipient's name, bank account details.</p>
          <p><strong>Financial Information:</strong> Proof of source of funds, bank account used.</p>
          <p><strong>Technical Information:</strong> IP address, activity logs, device and application data.</p>
          <p><strong>Other Information</strong> required in accordance with laws and regulations on Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF).</p>
          
          <h6 class="fw-bold mt-4">3. Purpose of Data Collection</h6>
          <p>The personal data collected will be used for the following purposes:</p>
          <p>• To provide, manage, and improve {{ brandName }}' services.</p>
          <p>• To carry out identity verification and Know Your Customer (KYC) processes.</p>
          <p>• To comply with applicable laws and regulations (including AML-CTF).</p>
          <p>• To detect and prevent fraud, misuse, or other illegal activities.</p>
          <p>• To provide information to competent authorities when legally required.</p>
          <p>• To communicate important updates regarding transactions or services.</p>
          
          <h6 class="fw-bold mt-4">4. Legal Basis for Data Processing</h6>
          <p>We process personal data based on the following legal grounds:</p>
          <p>• The User's consent.</p>
          <p>• Legal obligations (including regulations from Bank Indonesia, PPATK, and other applicable laws).</p>
          <p>• Legitimate interests of the company in ensuring security and operational efficiency.</p>
          <p>• Contractual obligations between {{ brandName }} and the User.</p>
          
          <h6 class="fw-bold mt-4">5. Data Storage and Protection</h6>
          <p>• Data is stored on servers protected both physically and digitally, in accordance with industry standards.</p>
          <p>• {{ brandName }} implements encryption, firewalls, and access controls to prevent unauthorized access.</p>
          <p>• Data will be retained as long as necessary for processing purposes or as permitted by law.</p>
          
          <h6 class="fw-bold mt-4">6. Sharing of Personal Data</h6>
          <p>{{ brandName }} may share personal data with the following third parties:</p>
          <p>• Overseas correspondent partners for international remittance purposes.</p>
          <p>• Domestic correspondent partners for domestic fund transfers.</p>
          <p>• Verification and KYC service providers.</p>
          <p>• Regulatory and legal authorities (Bank Indonesia, PPATK, Police, etc.).</p>
          <p>All third parties are required to adhere to data protection principles equivalent to our own.</p>
          
          <h6 class="fw-bold mt-4">7. User Rights Regarding Personal Data</h6>
          <p>Users have the right to:</p>
          <p>• Access and obtain a copy of their personal data.</p>
          <p>• Update or correct inaccurate personal data.</p>
          <p>• Withdraw consent (with certain consequences on the services).</p>
          <p>• Request deletion of personal data, in accordance with applicable laws and regulations.</p>
          <p>Requests regarding these rights may be submitted via: <strong>privacy@swiftrans.co.id</strong></p>
          
          <h6 class="fw-bold mt-4">8. Cookies and Similar Technologies</h6>
          <p>We use cookies and similar technologies to:</p>
          <p>• Enhance user experience.</p>
          <p>• Collect data on service usage.</p>
          <p>• Provide more relevant content.</p>
          <p>Users can manage their cookie preferences through their respective browser settings.</p>
          
          <h6 class="fw-bold mt-4">9. Changes to the Privacy Policy</h6>
          <p>{{ brandName }} may update this Privacy Policy from time to time. We will notify Users of any material changes via the application, email, or other official communication channels.</p>
          <p>Continued use of the services following any changes will constitute acceptance of the updated Privacy Policy.</p>
          
          <h6 class="fw-bold mt-4">10. Contact Information</h6>
          <p>For any questions, information requests, or complaints regarding this Privacy Policy, please contact us at:</p>
          <p><strong>PT Anugrah Multi Remittance ({{ brandName }})</strong></p>
          <p><strong>Office Address:</strong> Komplek Bumi Indah Blok F No. 2, Lubuk Baja Kota, Lubuk Baja District, Batam City</p>
          <p><strong>Email:</strong> cs@swiftrans.co.id</p>
          
          <h6 class="fw-bold mt-4">11. Governing Law</h6>
          <p>This Policy shall be governed by and construed in accordance with the laws of the Republic of Indonesia, including Law Number 27 of 2022 on Personal Data Protection, and its implementing regulations.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showPrivacyModal = false">Close</button>
          <button type="button" class="btn btn-success" @click="acceptPrivacy">I Understand</button>
        </div>
      </div>
    </div>
  </div>

  <Loading :isLoading="isLoading" />
</template>

<script>
import { onBeforeUnmount, onBeforeMount } from "vue";
import { useStore } from "vuex";
import ArgonInput from "@/components/ArgonInput";
import ArgonButton from "@/components/ArgonButton";
import { buildFormData } from "../../utils/param-builder";
import { registerAccount } from "../../api/auth";
import Loading from "@/components/Loading";
import TermsAndConditionsModal from "@/components/TermsAndConditionsModal";
import { useBranding } from "@/utils/branding";

export default {
  components: {
    ArgonInput,
    ArgonButton,
    Loading,
    TermsAndConditionsModal,
  },
  data() {
    const { brandLogo, brandName } = useBranding();
    return {
      fullname: '',
      email: '',
      phone: '',
      company_name: '',
      password: '',
      confirm_password: '',
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      errors: {},
      generalError: '',
      agreedToTerms: false,
      showTermsModal: false,
      showPrivacyModal: false,
      brandLogo,
      brandName,
      cf_turnstile_response: '',
      turnstileWidgetId: null,
    };
  },
  methods: {
    clearErrors() {
      this.errors = {};
      this.generalError = '';
    },
    formatPhoneNumber(event) {
      this.phone = event.target.value.replace(/\D/g, '');
    },
    async submitForm() {
      this.clearErrors();
      if (!this.validate()) {
        return;
      }

      this.isLoading = true;

      const params = {
        fullname: this.fullname,
        email: this.email,
        company_name: this.company_name,
        phone: this.phone,
        password: this.password,
        password_confirmation: this.confirm_password,
        cf_turnstile_response: this.cf_turnstile_response,
      };

      try {
        const formData = buildFormData(params);
        const response = await registerAccount(formData);

        if (response.status === "success") {
          this.$store.commit("auth/setEmail", this.email);
          this.$router.push({ name: "VerifyEmail" });
        } else {
          this.generalError = response.message || "Registration failed";
          this.resetTurnstile();
        }
      } catch (error) {
        this.resetTurnstile();
        if (error.response) {
          const errorData = error.response.data;

          // Handle validation errors (422)
          if (error.response.status === 422 && errorData.data) {
            this.errors = errorData.data;
            this.generalError = errorData.message || 'Please correct the errors below.';
          } else {
            // Handle other error responses
            this.generalError = errorData.message || "Registration failed. Please try again.";
          }
        } else {
          this.generalError = "An unexpected error occurred. Please try again.";
        }
        this.isLoading = false;
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.fullname = "";
      this.email = "";
      this.phone = "";
      this.company_name = "";
      this.password = "";
      this.confirm_password = "";
      this.agreedToTerms = false;
      this.resetTurnstile();
    },
    validate() {
      let isValid = true;
      this.clearErrors();

      // Fullname validation
      if (!this.fullname) {
        this.errors.fullname = ["Fullname is required"];
        isValid = false;
      } else if (this.fullname.length < 2 || this.fullname.length > 100) {
        this.errors.fullname = ["Fullname must be between 2 and 100 characters"];
        isValid = false;
      }

      // Email validation
      if (!this.email) {
        this.errors.email = ["Email is required"];
        isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          this.errors.email = ["Please enter a valid email address"];
          isValid = false;
        }
      }

      // Company name validation
      if (!this.company_name) {
        this.errors.company_name = ["Company name is required"];
        isValid = false;
      } else if (this.company_name.length < 2 || this.company_name.length > 100) {
        this.errors.company_name = ["Company name must be between 2 and 100 characters"];
        isValid = false;
      }

      // Phone validation
      if (!this.phone) {
        this.errors.phone = ["Phone number is required"];
        isValid = false;
      }

      // Password validation
      if (!this.password) {
        this.errors.password = ["Password is required"];
        isValid = false;
      } else if (this.password.length < 8) {
        this.errors.password = ["Password must be at least 8 characters long"];
        isValid = false;
      } else {
        const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+=\-[\]{};:"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+=\-[\]{};:"\\|,.<>/?]{8,}$/;
        if (!passwordRegex.test(this.password)) {
          this.errors.password = ["Password must contain at least one uppercase letter, one number, and one special character"];
          isValid = false;
        }
      }

      // Confirm password validation
      if (!this.confirm_password) {
        this.errors.password_confirmation = ["Confirm password is required"];
        isValid = false;
      } else if (this.password !== this.confirm_password) {
        this.errors.password_confirmation = ["Passwords do not match"];
        isValid = false;
      }

      // Terms & Conditions validation
      if (!this.agreedToTerms) {
        this.errors.agreedToTerms = ["You must agree to the Terms & Conditions and Privacy Policy"];
        isValid = false;
      }

      // Cloudflare Turnstile validation
      if (!this.cf_turnstile_response) {
        this.errors.cf_turnstile_response = ["Please complete the security check."];
        isValid = false;
      }

      return isValid;
    },
    togglePasswordVisibility(field) {
      if (field === 'password') {
        this.showPassword = !this.showPassword;
      } else if (field === 'confirmPassword') {
        this.showConfirmPassword = !this.showConfirmPassword;
      }
    },
    openTermsModal() {
      this.showTermsModal = true;
    },
    openPrivacyModal() {
      this.showPrivacyModal = true;
    },
    acceptTerms() {
      this.agreedToTerms = true;
      if (this.errors.agreedToTerms) {
        delete this.errors.agreedToTerms;
      }
    },
    acceptPrivacy() {
      this.showPrivacyModal = false;
    },
    initTurnstile() {
      // Check if turnstile script is already loaded
      if (document.getElementById('cloudflare-turnstile-script')) {
        this.renderTurnstile();
        return;
      }

      // Dynamically load the turnstile script
      const script = document.createElement('script');
      script.id = 'cloudflare-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
      script.async = true;
      script.defer = true;
      
      // Define the global callback that Turnstile calls once the script is loaded
      window.onloadTurnstileCallback = () => {
        this.renderTurnstile();
      };
      
      document.head.appendChild(script);
    },
    renderTurnstile() {
      if (window.turnstile) {
        const siteKey = process.env.VUE_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
        try {
          this.turnstileWidgetId = window.turnstile.render('#turnstile-container', {
            sitekey: siteKey,
            callback: (token) => {
              this.cf_turnstile_response = token;
              if (this.errors.cf_turnstile_response) {
                delete this.errors.cf_turnstile_response;
              }
            },
            'expired-callback': () => {
              this.cf_turnstile_response = '';
            },
            'error-callback': () => {
              this.cf_turnstile_response = '';
            }
          });
        } catch (e) {
          console.error("Turnstile render error", e);
        }
      }
    },
    resetTurnstile() {
      if (window.turnstile && this.turnstileWidgetId !== null) {
        try {
          window.turnstile.reset(this.turnstileWidgetId);
        } catch (e) {
          console.error("Turnstile reset error", e);
        }
      }
      this.cf_turnstile_response = '';
    },
  },
  mounted() {
    this.initTurnstile();
  },
  beforeUnmount() {
    if (window.turnstile && this.turnstileWidgetId !== null) {
      try {
        window.turnstile.remove(this.turnstileWidgetId);
      } catch (e) {
        console.error("Turnstile remove error", e);
      }
    }
  },
  setup() {
    const store = useStore();
    const body = document.getElementsByTagName("body")[0];

    onBeforeMount(() => {
      store.state.hideConfigButton = true;
      store.state.showNavbar = false;
      store.state.showSidenav = false;
      store.state.showFooter = false;
      body.classList.remove("bg-gray-100");
    });

    onBeforeUnmount(() => {
      store.state.hideConfigButton = false;
      store.state.showNavbar = true;
      store.state.showSidenav = true;
      store.state.showFooter = true;
      body.classList.add("bg-gray-100");
    });
  },
};
</script>
